import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { Command, CommandBase } from '../../../core/model/command';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { LocalDate } from '../../../core/model/local-date';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { SidebarService } from '../../../core/service/sidebar.service';
import { LeaseAgreementInformationRow } from '../../model/lease-agreement-information-row';
import { LeaseAgreementInformationRowService } from '../../service/data/lease-agreement-information-row.service';

enum InformationRowCommands {
  edit = 'editLeaseAgreementInformationRow',
  delete = 'deleteLeaseAgreementInformationRow',
}

@Component({
  selector: 'app-lease-agreement-information-rows',
  templateUrl: './lease-agreement-information-rows.component.html',
  styleUrls: ['./lease-agreement-information-rows.component.scss'],
  providers: [CommandDispatcher],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LeaseAgreementInformationRowsComponent extends CommandController implements OnInit, OnDestroy {
  @ViewChild('informationRowSidebar', { static: false }) informationRowSidebar: TemplateRef<any>;
  @Input() entityId: string;

  readonly columns: ColumnDefinition[];
  readonly menu = [
    new MenuItemDefinition('EDIT_BTN', InformationRowCommands.edit),
    new MenuItemDefinition('DELETE_BTN', InformationRowCommands.delete),
  ];
  readonly displayForm: FormGroup;
  form: FormGroup;
  allRows: LeaseAgreementInformationRow[] = [];
  visibleRows: LeaseAgreementInformationRow[] = [];
  selectedRow: LeaseAgreementInformationRow;
  saving = false;
  private readonly _informationRowSubscriptions = new Subscription();

  constructor(
    handlerResolver: CommandHandlerResolver,
    commandDispatcher: CommandDispatcher,
    columnFactory: ColumnDefinitionFactory,
    private readonly _fb: FormBuilder,
    private readonly _controls: FormControlExFactory,
    private readonly _service: LeaseAgreementInformationRowService,
    private readonly _sidebar: SidebarService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    super(commandDispatcher, handlerResolver);
    this.columns = [
      columnFactory.create('LEASE_AGREEMENT_INFORMATION_ROW.TEXT', 'text'),
      columnFactory.createDate('LEASE_AGREEMENT_INFORMATION_ROW.FROM_DATE', 'fromDate'),
      columnFactory.createDate('LEASE_AGREEMENT_INFORMATION_ROW.TO_DATE', 'toDate'),
    ];
    this.displayForm = this._fb.group({
      displayHistory: this._controls.create('COMMON.DISPLAY_HISTORY', false),
    });
  }

  ngOnInit(): void {
    this._informationRowSubscriptions.add(
      this.displayForm.controls['displayHistory'].valueChanges.subscribe(() => this.applyHistoryFilter())
    );
    this._informationRowSubscriptions.add(
      this._service.rowsChanged$.subscribe(change => {
        if (change.leaseAgreementId === this.entityId) this.setRows(change.rows);
      })
    );
    this._service.getRows(this.entityId).subscribe();
  }

  override ngOnDestroy(): void {
    this._informationRowSubscriptions.unsubscribe();
    super.ngOnDestroy();
  }

  canExecute(command: CommandBase): boolean {
    return command.id === InformationRowCommands.edit || command.id === InformationRowCommands.delete;
  }

  execute(command: CommandBase): void {
    const row = (command as Command).context as LeaseAgreementInformationRow;
    if (command.id === InformationRowCommands.edit) this.openForm(row);
    if (command.id === InformationRowCommands.delete) this._service.deleteRow(this.entityId, row.id).subscribe();
  }

  createRow(): void {
    const today = LocalDate.today();
    const nextMonth = today.month === 12 ? 1 : today.month + 1;
    const nextMonthYear = today.month === 12 ? today.year + 1 : today.year;
    this.openForm(new LeaseAgreementInformationRow({
      fromDate: new LocalDate(nextMonthYear, nextMonth, 1),
    }));
  }

  openForm(row: LeaseAgreementInformationRow): void {
    this.selectedRow = new LeaseAgreementInformationRow(row);
    this.form = this._fb.group(
      {
        text: this._controls.createWithOptions('LEASE_AGREEMENT_INFORMATION_ROW.TEXT', this.selectedRow.text, {
          required: true,
          length: 200,
          validator: this.nonWhitespaceValidator(),
        }),
        fromDate: this._controls.createWithOptions('LEASE_AGREEMENT_INFORMATION_ROW.FROM_DATE', this.selectedRow.fromDate, {
          required: true,
        }),
        toDate: this._controls.create('LEASE_AGREEMENT_INFORMATION_ROW.TO_DATE', this.selectedRow.toDate),
      },
      { validators: this.dateRangeValidator() }
    );
    this._sidebar.setSidebarContent({ template: this.informationRowSidebar });
    this._sidebar.toggleSidebar(true);
  }

  onRowClicked(event: { context: LeaseAgreementInformationRow }): void {
    this.openForm(event.context);
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;

    this.selectedRow.text = this.form.controls['text'].value;
    this.selectedRow.fromDate = this.form.controls['fromDate'].value;
    this.selectedRow.toDate = this.form.controls['toDate'].value;
    this.saving = true;

    if (!this.selectedRow.id) {
      this._service
        .createRow(this.entityId, this.selectedRow)
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: createdRow => {
            this.selectedRow.id = createdRow.id;
            this.selectedRow.sortOrder = createdRow.sortOrder;
            this.setRows([...this.allRows, this.selectedRow]);
            this.closeForm();
          },
          error: () => undefined,
        });
      return;
    }

    this._service
      .updateRow(this.entityId, this.selectedRow)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: () => this.closeForm(),
        error: () => undefined,
      });
  }

  closeForm(): void {
    this._sidebar.toggleSidebar(false);
    this.selectedRow = null;
    this.form = null;
  }

  drop(_event: CdkDragDrop<LeaseAgreementInformationRow>): void {
    const previousRows = [...this.allRows];
    let visibleIndex = 0;
    const merged = this.allRows.map(row =>
      this.isHistorical(row) && !this.displayForm.controls['displayHistory'].value
        ? row
        : this.visibleRows[visibleIndex++]
    );
    this.allRows = merged;
    this._service.reorderRows(this.entityId, merged.map(row => row.id)).subscribe({
      error: () => {
        this.allRows = previousRows;
        this.applyHistoryFilter();
      },
    });
  }

  previewText(row: LeaseAgreementInformationRow): string {
    return row.text;
  }

  private setRows(rows: LeaseAgreementInformationRow[]): void {
    this.allRows = [...rows].sort((left, right) => left.sortOrder - right.sortOrder);
    this.applyHistoryFilter();
  }

  private applyHistoryFilter(): void {
    const showHistory = this.displayForm.controls['displayHistory'].value;
    this.visibleRows = showHistory ? [...this.allRows] : this.allRows.filter(row => !this.isHistorical(row));
    this._cdr.detectChanges();
  }

  private isHistorical(row: LeaseAgreementInformationRow): boolean {
    return !!row.toDate?.hasValue() && row.toDate.valueOf() < LocalDate.today().valueOf();
  }

  private nonWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null =>
      typeof control.value === 'string' && control.value.trim().length > 0 ? null : { required: true };
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const from = LocalDate.fromDate(control.get('fromDate')?.value);
      const to = LocalDate.fromDate(control.get('toDate')?.value);
      if (!from?.hasValue() || !to?.hasValue() || to.valueOf() >= from.valueOf()) return null;
      return { dateRange: true };
    };
  }
}
